import { useReactiveVar } from '@apollo/client'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { Divider } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import SettingsIcon from '@material-ui/icons/Settings'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { reactiveWorkspaceActions } from 'api/graphQL/reactiveVars/workspaceActions'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { ActionButton } from 'components/ActionBar'
import SMMActionButtons from 'components/Workspace2ActionBar/SMMActionButtons'
import CopyIcon from 'components/Workspace2EditorActionBar/CopyIcon'
import IndentIcon from 'components/Workspace2EditorActionBar/IndentIcon'
import OutdentIcon from 'components/Workspace2EditorActionBar/OutdentIcon'
import { useEditorActionBarHandler } from 'components/WorkspaceEditor/hooks/useEditorActionBarHandler'
import { useWorkspace2ActionBarStyles } from 'components/WorkspaceEditor/styles/useWorkspace2ActionBarStyles'
import DuplicateProductTypeButton from 'pages/ProjectsPage/WorkspacePage/components/DuplicateProductTypeButton'
import {
    ActionButtonVariant,
    PROPERTY_EDITOR_SHORTCUT_KEY_MAPPING as ShortcutKeys,
} from 'pages/ProjectsPage/WorkspacePage/components/constants'
import React, { useCallback, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { VisibilitySettingsPopover } from './components/VisibilitySettings/VisibilitySettingsPopover'
import { useSyncInactiveContentVisibility } from './hooks/useSyncInactiveContentVisibility'
import { useShallowEditorActionBar } from './store/EditorActionBarStore'

export interface Workspace2EditorActionBarItem {
    label?: string
    stateKey: string
}

export interface Workspace2EditorActionBarProps {
    isLoading: boolean
    insertNewProductTypeElement?: (
        element: ProjectElementNode,
        isSelectedFromListingDialog?: boolean,
        newNodesForAdditionalInstances?: ProjectElementNode[]
    ) => void
}

interface LocationState {
    from: string
    fromStudio?: boolean
    selectedElementIdFromDesignProgressTable?: string
}

const Workspace2EditorActionBar = ({
    isLoading,
    insertNewProductTypeElement,
}: Workspace2EditorActionBarProps) => {
    const { pathname, state } = useLocation()
    const visibilitySettingsRef = useRef<HTMLDivElement>(null)
    const { currentUserActiveFirm } = React.useContext(AppBarContext)

    useSyncInactiveContentVisibility()

    const isUserRedirectedFromStudio = React.useMemo(
        () =>
            (state as LocationState)?.fromStudio ||
            pathname.includes('/DraftMasterSpecProject/') ||
            pathname.includes(
                `/${currentUserActiveFirm?.bestPracticeProjectId}/`
            ),
        [state, pathname]
    )
    const classes = useWorkspace2ActionBarStyles({
        isOMorSMM: isUserRedirectedFromStudio,
    })
    const {
        isVisibilitySettingsMenuDisplayed,
        actions: { setIsVisibilitySettingsMenuDisplayed },
    } = useShallowEditorActionBar((state) => ({
        isVisibilitySettingsMenuDisplayed:
            state.isVisibilitySettingsMenuDisplayed,
        actions: state.actions,
    }))
    const {
        canDemote,
        canPromote,
        isMutationInFlight,
        handlePromoteDemoteMutation,
        isCopyProductTypeEnabled,
    } = useEditorActionBarHandler()

    const { showSMMActionButtons } = useReactiveVar(reactiveWorkspaceActions)

    const { Demote, Promote, CopyProductType, Settings } = ActionButtonVariant

    const { parentFamilyId: projectProductFamilyId } = useElementItemShallowSelector(
        (s) => ({ parentFamilyId: s.elementItemValue.parentFamilyId })
    )
    const { selectedElementNode: selectedProjectElementNode } = useReactiveVar(
        workspaceSelectedElement
    )
    const handleVisibilityIconClick = useCallback(() => {
        setIsVisibilitySettingsMenuDisplayed(!isVisibilitySettingsMenuDisplayed)
    }, [
        isVisibilitySettingsMenuDisplayed,
        setIsVisibilitySettingsMenuDisplayed,
    ])

    return (
        <GridContainer className={classes.editorContentContainer}>
            <GridItem className={classes.roundContainerActionBar}>
                <span className={isMutationInFlight ? classes.inFlightButtonsWrapper : undefined}>
                    <ActionButton
                        onClick={() => handlePromoteDemoteMutation(Promote)}
                        loading={isLoading}
                        disabled={!canPromote || isLoading}
                        variant={Promote}
                        tooltip={{
                            label: 'Outdent',
                            smallLabel: ShortcutKeys.Promote,
                        }}
                        iconOverlaySize={22}
                        aria-label="Outdent"
                    >
                        <OutdentIcon
                            className={
                                isLoading
                                    ? classes.iconButtonDisabled
                                    : classes.iconButton
                            }
                        />
                    </ActionButton>
                </span>
                <span className={isMutationInFlight ? classes.inFlightButtonsWrapper : undefined}>
                    <ActionButton
                        onClick={() => handlePromoteDemoteMutation(Demote)}
                        loading={isLoading}
                        disabled={!canDemote || isLoading}
                        variant={Demote}
                        tooltip={{
                            label: 'Indent',
                            smallLabel: ShortcutKeys.Demote,
                        }}
                        iconOverlaySize={22}
                        aria-label="Indent"
                    >
                        <IndentIcon
                            className={
                                isLoading
                                    ? classes.iconButtonDisabled
                                    : classes.iconButton
                            }
                        />
                    </ActionButton>
                </span>
                {!isUserRedirectedFromStudio && (
                    <DuplicateProductTypeButton
                        selectedProjectElementNode={selectedProjectElementNode!}
                        projectProductFamilyId={projectProductFamilyId}
                        insertNewProductTypeElement={
                            insertNewProductTypeElement
                        }
                        actionButtonProps={{
                            tooltip: {
                                label: 'Copy',
                                smallLabel: ShortcutKeys.CopyPT,
                            },
                            variant: CopyProductType,
                            disabled: !isCopyProductTypeEnabled || isLoading,
                            iconOverlaySize: 22,
                            svgIcon: (
                                <CopyIcon
                                    className={
                                        isLoading
                                            ? classes.iconButtonDisabled
                                            : classes.iconButton
                                    }
                                />
                            ),
                        }}
                    />
                )}
                <Divider orientation="vertical" className={classes.divider} />
                {isUserRedirectedFromStudio && showSMMActionButtons && (
                    <>
                        <SMMActionButtons isNewWorkspaceActionButton />
                        <Divider
                            orientation="vertical"
                            className={classes.divider}
                        />
                    </>
                )}
                <ActionButton
                    onClick={handleVisibilityIconClick}
                    variant={Settings}
                    tooltip={{
                        label: 'Visibility Settings',
                    }}
                    aria-label="Visibility Settings"
                    hasMultipleIcons
                >
                    <SettingsIcon
                        className={classes.iconButtonVisibilitySettings}
                    />
                    <ExpandMoreIcon
                        innerRef={visibilitySettingsRef}
                        className={classes.visibilityExpandIcon}
                        htmlColor="#314959"
                    />
                </ActionButton>
            </GridItem>
            {isVisibilitySettingsMenuDisplayed && (
                <VisibilitySettingsPopover
                    elementRef={visibilitySettingsRef}
                    classes={classes}
                    isOpen={isVisibilitySettingsMenuDisplayed}
                />
            )}
        </GridContainer>
    )
}
export default Workspace2EditorActionBar
